import type { ConsensusBin, ConsensusCell, ConsensusSignal } from "../api/types";
import { zTone, zWord } from "./zscore";

/**
 * Разбор теневой ставки по КОТИРОВКЕ когорты плюсовых тейкеров для экрана.
 *
 * # Что этот раздел обязан сказать
 *
 * Панель участников показывает предсказание группы и молчит о том, стоит ли за
 * ним идти. Здесь та же котировка превращена в ставку и сведена по исходу окна,
 * и раздел обязан закрыть четыре ловушки, а не показать прибыль.
 *
 *   - ПРОТИВ ЧЕГО судить. Доля угаданного сама по себе не значит ничего. Она
 *     сравнивается с ценой входа (сколько правда стоила), с физикой (не купили
 *     ли мы то, что и так выигрывало) и — здесь это ново — С САМОЙ КОТИРОВКОЙ:
 *     сбывается ли то, что группа обещала своими деньгами.
 *   - СКОЛЬКО ГИПОТЕЗ. Клеток 18, ролей две, гипотез 36, и лучшая из них будет
 *     выглядеть кромкой ПО ПОСТРОЕНИЮ. Поэтому раздел говорит о ГЛАВНОЙ клетке,
 *     назначенной до первых данных, а не о максимуме по сетке.
 *   - КЛЕТКИ ВЛОЖЕНЫ. Строка с котировкой 0.72 попала в каждую клетку до 0.70,
 *     и сумма по столбцу посчитает её трижды. Складывать столбец нельзя, и
 *     сказать об этом надо словами.
 *   - НАЛИЛИ ЛИ БЫ, и ПОЧЁМ. Мейкер здесь преследует L1, а не стоит: если
 *     преследование подтягивает цену к аску, вся экономия мейкера сводится к
 *     комиссии — и вывод шире этой подсистемы.
 *
 * # Что в разделе ЕСТЬ с 2026-09-04
 *
 * Кнопки. Раньше здесь стояло «их нет и не будет: у подсистемы нет порта
 * постановки ордера». Порт появился (ADR-0027), и вместе с ним появились
 * переключатель живого режима в шапке панели и кнопка «взять» по строке ленты на
 * ступени `alert`. Арифметика показа от этого не изменилась ничем: она
 * по-прежнему считает ЗАМЕР, а не деньги.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Доля, посчитанная только при непустом знаменателе. Прочерк — «не из чего». */
export function ratio(ok: number, n: number): string {
  if (n <= 0) return "—";
  return `${Math.round((100 * ok) / n)} %`;
}

/** Деньги без копеек: ставка меряется тысячами, и центы в ней — шум. */
export function usd(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `$${Math.round(n).toLocaleString("ru-RU")}`;
}

/** Цена тремя знаками — как в панели участников, чтобы числа сверялись глазом. */
export function price(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return n.toFixed(3);
}

// Показ z-оценки — общий модуль: тем же запасом в сигмах меряется кромка у
// соседей, и планка «зелёное только от двух сигм» обязана быть одной на экран.
export { zTone, zWord };

/**
 * Клетка сетки словами: «0.65 / $1500 / N=2».
 *
 * Три координаты, а не одна: усреднённая по клеткам строка не отвечает на
 * вопрос «какой порог работает», ради которого сетка и заведена.
 */
export function cellLabel(c: {
  consensus_bp: number;
  stake_step_usd: number;
  agree_n: number;
}): string {
  return `${(c.consensus_bp / 10000).toFixed(2)} / $${c.stake_step_usd.toLocaleString("ru-RU")} / N=${c.agree_n}`;
}

/** Минута окна словами: «0-60» на экране ничего не говорит. */
export function bucketLabel(bucket: string): string {
  switch (bucket) {
    case "0-60":
      return "последняя минута";
    case "60-120":
      return "за 1–2 мин";
    case "120-180":
      return "за 2–3 мин";
    case "180+":
      return "раньше 3 мин";
    default:
      return bucket;
  }
}

/**
 * Порог значимости с поправкой на множественность.
 *
 * Гипотез вдвое больше клеток: у каждой две роли исполнения. Печатать для них
 * ту же двойку, что для одной клетки, значило бы объявлять кромкой максимум по
 * множеству, смещённый вверх по построению.
 */
export function zThreshold(cells: number): number {
  const n = Math.max(1, 2 * cells);
  if (n <= 1) return 1.96;
  const p = 0.025 / n;
  const t = Math.sqrt(-2 * Math.log(p));
  return t - (2.30753 + 0.27061 * t) / (1 + 0.99229 * t + 0.04481 * t * t);
}

/** Клетки ГЛАВНОЙ строки сетки: только они идут в приговор раздела. */
export function mainCells(cells: ConsensusCell[]): ConsensusCell[] {
  return cells.filter((c) => c.main_cell);
}

/** Сумма поля по клеткам. Считает только по переданным — вложенность на вызывающем. */
function total(cells: ConsensusCell[], pick: (c: ConsensusCell) => number): number {
  return cells.reduce((n, c) => n + pick(c), 0);
}

/**
 * Снос преследования: на сколько цена налива не дошла до аска.
 *
 * ГЛАВНОЕ число мейкерской половины. Стремится к нулю — преследование подтянуло
 * нашу цену к аску, и экономия мейкера свелась к комиссии. Прочерк при пустом
 * знаменателе: строк с наливом и известным аском может не быть вовсе, и это не
 * «снос нулевой».
 */
export function driftWord(cells: ConsensusCell[]): string {
  const known = total(cells, (c) => c.drift_known);
  if (known <= 0) return "—";
  const sum = cells.reduce((n, c) => n + (num(c.drift) ?? 0), 0);
  return (sum / known).toFixed(3);
}

/**
 * Цвет числа в отчёте тейкера.
 *
 * Оператор попросил, чтобы у КАЖДОЙ цифры был цвет, и правило одно на весь
 * отчёт, иначе соседние числа читались бы по разным шкалам:
 *
 *   - деньги и винрейт судятся ЗАПАСОМ В СИГМАХ против цены входа, а не знаком:
 *     зелёный — плюс при z ≥ 2, красный — минус при z ≤ −2, жёлтый — всё, что
 *     между, то есть «знак есть, значимости нет». На десятках строк знак суммы
 *     решают хвосты множителя выплаты, и красить его без z значило бы обещать
 *     кромку;
 *   - доли воронки судятся порогами 50 / 20 %: ступень, пропускающая больше
 *     половины, зелёная, меньше пятой части — красная;
 *   - комиссия судится долей от потраченного: до 1.5 % зелёная (тейкерская
 *     ставка на середине цены), до 3 % жёлтая, выше — красная.
 *
 * Возвращается CSS-переменная палитры или undefined — «цвет не назначен»,
 * когда считать не из чего; серый прочерк честнее любого цвета.
 */
export type Tone = "good" | "warn" | "bad";

export function toneColor(t: Tone | undefined): string | undefined {
  switch (t) {
    case "good":
      return "var(--color-good)";
    case "bad":
      return "var(--color-bad)";
    case "warn":
      return "var(--color-warn)";
    default:
      return undefined;
  }
}

/** Тон денег и винрейта: знак, взвешенный запасом в сигмах. */
export function edgeTone(value: number | null | undefined, z: number | null | undefined): Tone | undefined {
  if (value === null || value === undefined || !Number.isFinite(value)) return undefined;
  if (z === null || z === undefined) return value === 0 ? undefined : "warn";
  if (value > 0 && z >= 2) return "good";
  if (value < 0 && z <= -2) return "bad";
  return "warn";
}

/** Тон ступени воронки: какая доля предыдущей ступени прошла. */
export function shareTone(part: number, total: number): Tone | undefined {
  if (total <= 0) return undefined;
  const share = part / total;
  if (share >= 0.5) return "good";
  if (share >= 0.2) return "warn";
  return "bad";
}

/** Тон комиссии: доля от потраченного. */
export function feeTone(fees: string | null | undefined, cost: string | null | undefined): Tone | undefined {
  const f = num(fees);
  const c = num(cost);
  if (f === null || c === null || c <= 0) return undefined;
  const share = f / c;
  if (share <= 0.015) return "good";
  if (share <= 0.03) return "warn";
  return "bad";
}

/** Доля ступени словами: «21 из 29 · 72 %». */
export function stepWord(part: number, total: number): string {
  if (total <= 0) return `${part}`;
  return `${part} из ${total} · ${Math.round((100 * part) / total)} %`;
}

/** Правило отсева словами для воронки — то же, что в ленте. */
export function barrierLines(barriers: Record<string, string>): { label: string; value: string }[] {
  const order: [string, string][] = [
    ["consensus_mode", "режим"],
    ["consensus_scope", "область"],
    ["consensus_thresholds", "сетка"],
    ["consensus_cells", "клеток"],
    ["consensus_taker_limits", "тейкер"],
    ["consensus_rule", "группа"],
    ["consensus_roster", "состав"],
  ];
  return order.flatMap(([k, label]) => {
    const v = barriers[k];
    return v ? [{ label, value: v }] : [];
  });
}

/**
 * Оси сетки из списка клеток: уверенности, ставки, подтверждения — по
 * возрастанию, без повторов. Три селектора и строки/столбцы матрицы берут их
 * отсюда, поэтому оси у них одни и те же по построению.
 */
export function cellAxes(cells: { consensus_bp: number; stake_step_usd: number; agree_n: number }[]): {
  levels: number[];
  stakes: number[];
  agree: number[];
} {
  const uniq = (xs: number[]) => [...new Set(xs)].sort((a, b) => a - b);
  return {
    levels: uniq(cells.map((c) => c.consensus_bp)),
    stakes: uniq(cells.map((c) => c.stake_step_usd)),
    agree: uniq(cells.map((c) => c.agree_n)),
  };
}

/** Код клетки «6500-1500-2» — тот же, что принимает ручка. */
export function cellCode(bp: number, stake: number, agree: number): string {
  return `${bp}-${stake}-${agree}`;
}

/** Область отчёта словами: «BTC · 5m», «все активы · 15m». */
export function scopeWord(asset: string, window: string): string {
  return `${asset || "все активы"} · ${window || "все длительности"}`;
}

/** ROI процентом со знаком; прочерк без знаменателя, а не ноль. */
export function roiWord(roi: number | null | undefined): string {
  if (roi === null || roi === undefined || !Number.isFinite(roi)) return "—";
  const pct = 100 * roi;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return `${sign}${Math.abs(pct).toFixed(1)} %`;
}

/** Деньги с центами без знака: комиссия меряется центами, и «$0» её прячет. */
export function usdCents(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `$${n.toFixed(2)}`;
}

/** Деньги со знаком и центами: счёт тейкера меряется десятками долларов. */
export function signedUsd(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

/** Среднее по сумме контракта и знаменателю. Прочерк — «не из чего». */
export function meanPrice(sum: string, n: number): string {
  const v = num(sum);
  if (v === null || n <= 0) return "—";
  return (v / n).toFixed(3);
}

/**
 * Что калибровка говорит одной строкой.
 *
 * Берётся НИЖНЯЯ корзина с достаточным числом окон, а не лучшая: вопрос вехи
 * задан про уровень 0.60 («сбывается в 60 % или в 80 %»), и выбор корзины по
 * результату подменил бы его максимумом по набору. Доля побед печатается вместе
 * с аском и физикой той же секунды — без них она читается как кромка.
 */
export function calibrationGist(bins: ConsensusBin[], minWindows = 10): string {
  const b = [...bins].sort((a, c) => a.bin_bp - c.bin_bp).find((x) => x.windows >= minWindows);
  if (!b) return "калибровка: окон пока мало";
  return (
    `котировка ${b.bin}: сбылось ${ratio(b.won, b.windows)} из ${b.windows} окон ` +
    `при аске ${meanPrice(b.ask_sum, b.ask_known)} и физике ${meanPrice(b.phys_sum, b.phys_known)} в ту же секунду`
  );
}

/**
 * Приговор разделу одной строкой.
 *
 * Порядок ветвлений — часть смысла. Сначала называется то, что обнуляет замер
 * целиком (нечего сводить, подтверждение отравлено, меряется один кит), и
 * только потом кромка: раздел, начинающийся с «кромка +2.1σ» при медианной доле
 * кита 0.8, отправил бы оператора праздновать копирование одного кошелька.
 *
 * Говорит о ГЛАВНОЙ клетке. Максимум по сетке здесь запрещён: из 36 гипотез
 * лучшая смещена вверх по построению, и назвать её кромкой значило бы совершить
 * ровно ту ошибку, от которой назначение главной клетки защищает.
 */
export function verdict(cells: ConsensusCell[]): string {
  const main = mainCells(cells);
  const signals = total(main, (c) => c.signals);
  const suppressed = total(main, (c) => c.suppressed);
  if (main.length === 0) {
    return cells.length === 0 ? "строк ещё не было" : "главной клетки в выборке нет";
  }
  if (signals === 0 && suppressed === 0) return "строк ещё не было";

  const settled = total(main, (c) => c.settled);
  if (settled === 0) {
    return `${signals} зачётных, сведённых ещё нет — судить не по чему`;
  }
  const solo = medianOf(main, (c) => num(c.top_share_median));
  if (solo !== null && solo > 0.5) {
    return `меряется один кит: медианная доля крупнейшего ${Math.round(solo * 100)} %`;
  }
  const won = total(main, (c) => c.won);
  const priced = total(main, (c) => c.priced);
  if (priced === 0) return `${settled} сведённых, но цены входа нет ни у одной`;
  const sum = main.reduce((n, c) => n + (num(c.price_sum) ?? 0), 0);
  const edge = (100 * (won - sum)) / priced;
  return (
    `главная клетка: угадано ${ratio(won, settled)}, кромка против цены ` +
    `${edge > 0 ? "+" : ""}${edge.toFixed(1)} п.п. на ${priced} — ` +
    `порог значимости |z| ≈ ${zThreshold(gridSize(cells)).toFixed(1)}`
  );
}

/**
 * Сколько РАЗНЫХ клеток в сетке.
 *
 * Считается по тройке координат, а не по числу строк ответа: одна и та же
 * клетка приезжает несколькими строками — по одной на ведро минуты окна, — и
 * взяв длину списка, мы завысили бы число гипотез вчетверо и сделали бы порог
 * значимости строже, чем он есть.
 */
export function gridSize(cells: ConsensusCell[]): number {
  return new Set(
    cells.map((c) => `${c.consensus_bp}-${c.stake_step_usd}-${c.agree_n}`),
  ).size;
}

/** Медиана по клеткам. Не среднее: одна клетка на $40 тыс. даёт среднее, которого не бывает. */
function medianOf(
  cells: ConsensusCell[],
  pick: (c: ConsensusCell) => number | null,
): number | null {
  const xs = cells.map(pick).filter((x): x is number => x !== null && x > 0);
  if (xs.length === 0) return null;
  return xs.sort((a, b) => a - b)[Math.floor(xs.length / 2)] ?? null;
}

/** Доля крупнейшего участника в ставке группы. */
export function soloShare(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `${Math.round(n * 100)} %`;
}

export function soloTone(v: string | null | undefined): string | undefined {
  const n = num(v);
  if (n === null) return undefined;
  return n > 0.5 ? "var(--color-bad)" : undefined;
}

/** Фора до конца окна словами. Знаковая: сигнал законно попадает за конец окна. */
export function lead(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 0) return `после окна, ${-s} с`;
  if (s < 60) return `${s} с`;
  return `${Math.floor(s / 60)} мин ${s % 60} с`;
}

/**
 * Исход строки словами.
 *
 * «Угадано» здесь про КОТИРОВКУ, а не про наш карман: сторона могла выиграть,
 * а заявку могло не налить. Смешивать их нельзя — на этом разделении держится
 * вся приёмка подсистемы.
 */
export function outcomeWord(s: ConsensusSignal): string {
  if (!s.resolved) return "ждём";
  return s.won ? "угадано" : "мимо";
}

/** Правило, погасившее строку, словами. */
export function ruleWord(rule: string | null | undefined): string {
  switch (rule) {
    case "":
    case undefined:
    case null:
      return "";
    case "market":
      return "нет рынка";
    case "tokens":
      return "исходов не два";
    case "roster":
      return "подгруппа не собрана";
    case "thin":
      return "оборот окна ниже пола";
    case "settled":
      return "исход уже определён";
    case "late":
      return "форы не осталось";
    case "book":
      return "стакана не было";
    case "price_zone":
      return "цена вне зоны";
    case "min_size":
      return "нечего взять";
    default:
      return rule;
  }
}

/**
 * Что стало с преследующей заявкой, словами.
 *
 * Три состояния, и все три разные. Прочерк — ленту не наблюдали. «Не налили» —
 * РЕЗУЛЬТАТ замера, а не его отсутствие. Налив показывается вместе с ценой,
 * потому что цена постановки и цена налива у преследования разные, и разность
 * между ними — предмет вехи.
 */
export function chaseWord(s: ConsensusSignal): string {
  if (s.maker_filled === undefined || s.maker_filled === null) return "—";
  if (!s.maker_filled) {
    return s.maker_reprices > 0
      ? `не налили, ${s.maker_reprices} перестановок`
      : "не налили, бид не двигался";
  }
  const fill = num(s.maker_fill_price);
  const start = num(s.plan_price);
  if (fill === null || start === null) return "налили";
  const доплата = fill - start;
  return `налили по ${fill.toFixed(3)} (${доплата >= 0 ? "+" : ""}${доплата.toFixed(3)} к постановке)`;
}

/**
 * Подсказка к строке: чем котировка отличается от нетто и чем подтверждена.
 *
 * Ловушка оси называется ЗДЕСЬ, потому что на экране она невидима: сторона
 * `asset_id` предсказана ЦЕНОЙ, и она законно указывает против того, куда группа
 * встала по нетто.
 */
export function signalTitle(s: ConsensusSignal): string {
  const части = [
    `котировка ${price(s.consensus_price)} на ${s.outcome_name || s.asset_id}`,
    `ставка ${usd(s.stake_usd)} (${(s.stake_share_bp / 100).toFixed(1)} % оборота окна)`,
    `кошельков ${s.wallets}, крупнейший ${usd(s.top_wallet_usd)}`,
    `крупных согласных по оси ${s.big_axis_500}, из них по направлению ` +
      `${s.big_side_500}, из самой группы ${s.big_group_500}`,
    `клетка ${cellLabel(s)} из ${s.cells_total}${s.main_cell ? " (главная)" : ""}`,
    "сторона предсказана ЦЕНОЙ и законно может указывать против нетто-позиции группы",
  ];
  if (s.reversal) части.push("разворот: котировка перешла через половину");
  return части.join("\n");
}

/** Цвет строки ленты: погашенная бледнее, разворот заметнее. */
export function rowTone(s: ConsensusSignal): string | undefined {
  if (s.rule) return "var(--color-muted)";
  if (s.reversal) return "var(--color-warn)";
  return undefined;
}
